import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import backendAPI from '../../../utils/backendAPI'
import { syncMerchantRewardDocument } from '../../../utils/merchantRewardsFirestore'
import { Card, CardHeader, CardBody } from '../../../components/UI/card'
import { useTranslation } from 'react-i18next'

const EXPIRY_MINUTES = 3
const POLL_INTERVAL = 3000

function ValidatedRewards() {
	const { t } = useTranslation()
	const userLogin = useSelector((state) => state.userLogin)
	const { userInfo } = userLogin
	const isAdmin = userInfo?.is_admin || userInfo?.is_staff

	const [rewards, setRewards] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [debugInfo, setDebugInfo] = useState(null)
	const [now, setNow] = useState(Date.now())
	const [merchants, setMerchants] = useState([])
	const [selectedMerchantId, setSelectedMerchantId] = useState('')
	const [redeemingRewardId, setRedeemingRewardId] = useState(null)
	const [expiringRewardId, setExpiringRewardId] = useState(null)

	const [showCreateModal, setShowCreateModal] = useState(false)
	const [creatingReward, setCreatingReward] = useState(false)
	const [createRewardForm, setCreateRewardForm] = useState({
		reward_type: '',
		cost_points: 0,
		user_id: '',
	})

	const [currentMerchant, setCurrentMerchant] = useState(null)
	const [customers, setCustomers] = useState([])
	const [loadingCustomers, setLoadingCustomers] = useState(false)

	const fetchMerchants = useCallback(async () => {
		try {
			const { data } = await backendAPI.get('/api/square/merchants/all/')
			const merchantsList = data.merchants || []
			setMerchants(merchantsList)
			if (isAdmin) {
				if (merchantsList.length > 0) {
					setSelectedMerchantId((prev) => prev || merchantsList[0].merchant_id)
				}
			} else {
				if (merchantsList.length > 0) {
					setCurrentMerchant(merchantsList[0])
				}
			}
		} catch (err) {
			console.error('Error fetching merchants:', err)
		}
	}, [isAdmin])

	const fetchRewards = useCallback(async (merchantId = null) => {
		try {
			const url = merchantId
				? `/api/square/rewards/validated/?merchant_id=${merchantId}`
				: '/api/square/rewards/validated/'
			const { data } = await backendAPI.get(url)
			setRewards(data.rewards || [])
			setDebugInfo(data.debug || null)
			setError(null)
		} catch (err) {
			setError(t('validateRewards.fetchError', 'Failed to fetch rewards'))
			console.error('Error fetching rewards:', err)
		} finally {
			setLoading(false)
		}
	}, [t])

	const fetchCustomers = useCallback(async (merchantId = null) => {
		setLoadingCustomers(true)
		try {
			const url = merchantId
				? `/api/square/merchants/${merchantId}/customers/`
				: '/api/square/merchants/customers/'
			const { data } = await backendAPI.get(url)
			setCustomers(data.customers || [])
		} catch (err) {
			console.error('Error fetching customers:', err)
			setCustomers([])
		} finally {
			setLoadingCustomers(false)
		}
	}, [])

	const handleRedeem = async (rewardId) => {
		if (redeemingRewardId === rewardId) return
		setRedeemingRewardId(rewardId)
		try {
			const response = await backendAPI.post(`/api/square/rewards/${rewardId}/redeem/`)
			const updatedReward = response.data.reward || response.data
			setRewards((prev) => prev.map((r) => (r.reward_id === rewardId ? updatedReward : r)))
			if (isAdmin && selectedMerchantId) fetchRewards(selectedMerchantId)
			else fetchRewards()
		} catch (err) {
			console.error('Error redeeming reward:', err)
			if (err.response?.status === 400 || err.response?.status === 404) {
				if (isAdmin && selectedMerchantId) fetchRewards(selectedMerchantId)
				else fetchRewards()
			}
		} finally {
			setRedeemingRewardId(null)
		}
	}

	const handleValidate = async (rewardId) => {
		try {
			const response = await backendAPI.post(`/api/square/rewards/${rewardId}/validate/`)
			const updatedReward = response.data.reward || response.data
			setRewards((prev) => prev.map((r) => (r.reward_id === rewardId ? updatedReward : r)))
			if (isAdmin && selectedMerchantId) fetchRewards(selectedMerchantId)
			else fetchRewards()
		} catch (err) {
			console.error('Error validating reward:', err)
			if (err.response?.status === 400 || err.response?.status === 404) {
				if (isAdmin && selectedMerchantId) fetchRewards(selectedMerchantId)
				else fetchRewards()
			}
		}
	}

	const handleExpire = (rewardId) => {
		setExpiringRewardId(rewardId)
	}

	const confirmExpire = async () => {
		const rewardId = expiringRewardId
		setExpiringRewardId(null)
		try {
			await backendAPI.post(`/api/square/rewards/${rewardId}/expire/`)
			setRewards((prev) =>
				prev.map((r) => (r.reward_id === rewardId ? { ...r, status: 'EXPIRED' } : r))
			)
		} catch (err) {
			console.error('Error expiring reward:', err)
		}
	}

	const handleCreateReward = async (e) => {
		e.preventDefault()
		if (!createRewardForm.reward_type) return

		setCreatingReward(true)
		try {
			let merchantId = null
			if (isAdmin && selectedMerchantId) merchantId = selectedMerchantId
			else if (!isAdmin && currentMerchant) merchantId = currentMerchant.merchant_id
			else if (debugInfo?.merchant_id) merchantId = debugInfo.merchant_id

			const payload = {
				reward_type: createRewardForm.reward_type,
				cost_points: parseInt(createRewardForm.cost_points, 10) || 0,
			}
			if (merchantId) payload.merchant_id = merchantId
			if (createRewardForm.user_id) payload.user_id = parseInt(createRewardForm.user_id)

			const { data: created } = await backendAPI.post('/api/square/rewards/create/', payload)
			const rewardRow = created?.reward || created

			if (rewardRow && merchantId && (rewardRow.reward_id || rewardRow.id)) {
				await syncMerchantRewardDocument(merchantId, rewardRow)
			}

			setCreateRewardForm({ reward_type: '', cost_points: 0, user_id: '' })
			setShowCreateModal(false)

			if (isAdmin && selectedMerchantId) fetchRewards(selectedMerchantId)
			else fetchRewards()
		} catch (err) {
			console.error('Error creating reward:', err)
		} finally {
			setCreatingReward(false)
		}
	}

	useEffect(() => {
		if (showCreateModal) {
			if (isAdmin && selectedMerchantId) fetchCustomers(selectedMerchantId)
			else fetchCustomers()
		}
	}, [showCreateModal, isAdmin, selectedMerchantId, fetchCustomers])

	useEffect(() => {
		fetchMerchants()
	}, [fetchMerchants])

	useEffect(() => {
		if (isAdmin && !selectedMerchantId) return
		if (isAdmin && selectedMerchantId) fetchRewards(selectedMerchantId)
		else fetchRewards()

		const pollInterval = setInterval(() => {
			if (isAdmin && selectedMerchantId) fetchRewards(selectedMerchantId)
			else fetchRewards()
		}, POLL_INTERVAL)

		return () => clearInterval(pollInterval)
	}, [fetchRewards, isAdmin, selectedMerchantId])

	const handleMerchantChange = (e) => {
		const merchantId = e.target.value
		setSelectedMerchantId(merchantId)
		setLoading(true)
		fetchRewards(merchantId)
	}

	useEffect(() => {
		const tickInterval = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(tickInterval)
	}, [])

	const getTimeRemaining = (validatedAt) => {
		if (!validatedAt) return null
		const validatedTime = new Date(validatedAt).getTime()
		if (isNaN(validatedTime)) return null
		const expiryTime = validatedTime + EXPIRY_MINUTES * 60 * 1000
		return Math.max(0, expiryTime - now)
	}

	const formatTime = (ms) => {
		const totalSeconds = Math.floor(ms / 1000)
		const minutes = Math.floor(totalSeconds / 60)
		const seconds = totalSeconds % 60
		return `${minutes}:${seconds.toString().padStart(2, '0')}`
	}

	const statusCounts = useMemo(() => {
		const counts = { PENDING: 0, VALIDATED: 0, REDEEMED: 0, EXPIRED: 0 }
		rewards.forEach((reward) => {
			const status = reward.status?.toUpperCase() || ''
			if (Object.prototype.hasOwnProperty.call(counts, status)) counts[status]++
		})
		return counts
	}, [rewards])

	const getStatusBadge = (status) => {
		switch ((status || '').toUpperCase()) {
			case 'PENDING': return 'bg-soft-warning text-warning'
			case 'VALIDATED': return 'bg-soft-success text-success'
			case 'REDEEMED': return 'bg-soft-info text-info'
			case 'EXPIRED': return 'bg-soft-danger text-danger'
			default: return 'bg-soft-secondary text-secondary'
		}
	}

	const getStatusIcon = (status) => {
		switch ((status || '').toUpperCase()) {
			case 'PENDING': return 'mdi-clock-outline'
			case 'VALIDATED': return 'mdi-check-circle'
			case 'REDEEMED': return 'mdi-check-all'
			case 'EXPIRED': return 'mdi-close-circle'
			default: return 'mdi-help-circle'
		}
	}

	const formatTimestamp = (timestamp) => {
		if (!timestamp) return '-'
		return new Date(timestamp).toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		})
	}

	const closeCreateModal = () => {
		if (creatingReward) return
		setShowCreateModal(false)
		setCreateRewardForm({ reward_type: '', cost_points: 0, user_id: '' })
	}

	const allRewards = rewards

	return (
		<div className="content-page">
			<div className="content">
				<div className="container-fluid">
					<br />

					<div className="d-flex justify-content-between align-items-center mb-4">
						<div>
							<h1 style={{ color: '#313a46', fontWeight: 600 }}>
								<i className="mdi mdi-gift me-2" style={{ color: '#727cf5' }}></i>
								{t('validateRewards.header')}
							</h1>
							{isAdmin && selectedMerchantId && debugInfo?.business_name && (
								<p className="text-muted mb-0 mt-1" style={{ fontSize: '0.95rem' }}>
									<i className="mdi mdi-store me-1"></i>
									{debugInfo.business_name}
								</p>
							)}
						</div>

						<div className="d-flex align-items-center gap-3">
							{isAdmin && merchants.length > 0 && (
								<div style={{ minWidth: '250px' }}>
									<label className="form-label mb-1" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
										<i className="mdi mdi-store me-1"></i>
										{t('validateRewards.selectMerchant')}
									</label>
									<select
										className="form-select"
										value={selectedMerchantId}
										onChange={handleMerchantChange}
										style={{ fontSize: '0.95rem' }}
									>
										{merchants.map((merchant) => (
											<option key={merchant.merchant_id} value={merchant.merchant_id}>
												{merchant.business_name || merchant.merchant_id}
												{merchant.username ? ` (${merchant.username})` : ''}
											</option>
										))}
									</select>
								</div>
							)}

							<button
								className="btn btn-primary"
								onClick={() => setShowCreateModal(true)}
								style={{ padding: '10px 20px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}
							>
								<i className="mdi mdi-plus-circle"></i>
								{t('validateRewards.createReward')}
							</button>

							<div className="badge bg-soft-primary text-primary" style={{ fontSize: '1rem', padding: '8px 16px' }}>
								<i className="mdi mdi-sync me-1"></i>
								{t('validateRewards.autoRefreshing')}
							</div>
						</div>
					</div>

					{loading && rewards.length === 0 ? (
						<Card role="status" aria-live="polite">
							<CardBody className="text-center py-5">
								<div className="spinner-border text-primary" role="status" aria-hidden="true"></div>
								<p className="mt-3 text-muted mb-0">{t('validateRewards.loadingRewards')}</p>
							</CardBody>
						</Card>
					) : error ? (
						<div className="alert alert-danger">
							{error}
							{debugInfo && (
								<div className="mt-2" style={{ fontSize: '0.9rem' }}>
									<strong>{t('validateRewards.debugInfo')}:</strong>
									<pre style={{ marginTop: '8px', fontSize: '0.85rem' }}>
										{JSON.stringify(debugInfo, null, 2)}
									</pre>
								</div>
							)}
						</div>
					) : allRewards.length === 0 ? (
						<>
							{debugInfo && (
								<div className="alert alert-info mb-3">
									<strong>{t('validateRewards.debugInformation')}:</strong>
									<ul className="mb-0 mt-2">
										<li>{t('validateRewards.MerchantFound')}: {debugInfo.merchant_found ? 'Yes' : 'No'}</li>
										{debugInfo.merchant_id && <li>{t('merchantsData.merchantDetail.id')}: {debugInfo.merchant_id}</li>}
										{debugInfo.business_name && <li>{t('validateRewards.BusinessName')}: {debugInfo.business_name}</li>}
										{debugInfo.total_merchant_rewards !== undefined && (
											<li>{t('validateRewards.totalrewards')}: {debugInfo.total_merchant_rewards}</li>
										)}
										{debugInfo.status_counts && (
											<li>
												{t('validateRewards.statusBreakdown')}: {debugInfo.status_counts.PENDING || 0},{' '}
												{t('validateRewards.Validated')}: {debugInfo.status_counts.VALIDATED || 0},{' '}
												{t('validateRewards.Redeemed')}: {debugInfo.status_counts.REDEEMED || 0},{' '}
												{t('validateRewards.Expired')}: {debugInfo.status_counts.EXPIRED || 0}
											</li>
										)}
										{debugInfo.total_user_rewards !== undefined && (
											<li>{t('validateRewards.TotalUserRewards')}: {debugInfo.total_user_rewards}</li>
										)}
										{debugInfo.message && <li>{debugInfo.message}</li>}
									</ul>
								</div>
							)}
							<Card>
								<CardBody className="text-center py-5">
									<i className="mdi mdi-gift-outline" style={{ fontSize: '4rem', color: '#adb5bd' }}></i>
									<h4 className="mt-3 text-muted">{t('validateRewards.NoRewards')}</h4>
									<p className="text-muted mb-0">{t('validateRewards.message')}</p>
								</CardBody>
							</Card>
						</>
					) : (
						<Card>
							<CardHeader
								title={t('validateRewards.header')}
								subtitle={t('validateRewards.subtitle', 'View, validate, redeem, and expire customer rewards.')}
							/>
							<CardBody className="p-0">
								<div className="table-responsive">
									<table className="table table-hover mb-0">
										<thead className="table-light">
										<tr>
											<th style={{ padding: '16px' }}>{t('validateRewards.Customer')}</th>
											<th style={{ padding: '16px' }}>{t('validateRewards.Reward')}</th>
											<th style={{ padding: '16px' }}>{t('merchantsData.paymentDetail.status')}</th>
											<th style={{ padding: '16px' }}>{t('validateRewards.Points')}</th>
											<th style={{ padding: '16px' }}>{t('validateRewards.Created')}</th>
											<th style={{ padding: '16px', textAlign: 'center' }}>{t('validateRewards.ExpiresIn')}</th>
											<th style={{ padding: '16px', textAlign: 'center' }}>{t('validateRewards.Action')}</th>
										</tr>
										</thead>
										<tbody>
										{allRewards.map((reward) => {
											const timeRemaining = getTimeRemaining(reward.validated_at)
											const hasTimestamp = timeRemaining !== null
											const isUrgent = hasTimestamp && timeRemaining < 60000
											const progressPercent = hasTimestamp
												? (timeRemaining / (EXPIRY_MINUTES * 60 * 1000)) * 100
												: 100
											const rewardStatus = (reward.status || '').toUpperCase()
											const isExpired = rewardStatus === 'EXPIRED'
											const isRedeemed = rewardStatus === 'REDEEMED'
											const isValidated = rewardStatus === 'VALIDATED'
											const isPending = rewardStatus === 'PENDING'

											return (
												<tr key={reward.reward_id} style={{ transition: 'all 0.3s ease', opacity: isExpired ? 0.6 : 1 }}>
													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
														<div className="d-flex align-items-center">
															<div
																className="avatar-sm bg-soft-primary rounded-circle d-flex align-items-center justify-content-center me-2"
																style={{ width: '40px', height: '40px' }}
															>
																<i className="mdi mdi-account text-primary"></i>
															</div>
															<span className="fw-medium">
																	{reward.username || t('validateRewards.unknownCustomer', 'Unknown Customer')}
																</span>
														</div>
													</td>

													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
															<span className="badge bg-soft-info text-info">
																{reward.reward_type_display || reward.reward_type}
															</span>
													</td>

													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
															<span className={`badge ${getStatusBadge(reward.status)}`}>
																<i className={`mdi ${getStatusIcon(reward.status)} me-1`}></i>
																{reward.status_display || reward.status}
															</span>
													</td>

													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
														<span className="fw-medium">{reward.cost_points} pts</span>
													</td>

													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
														{formatTimestamp(reward.created_at)}
													</td>

													<td style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
														{isValidated ? (
															<div style={{ minWidth: '100px' }}>
																{hasTimestamp ? (
																	<>
																		<div className={`fw-bold ${isUrgent ? 'text-danger' : 'text-warning'}`} style={{ fontSize: '1.1rem' }}>
																			{formatTime(timeRemaining)}
																		</div>
																		<div className="progress mt-1" style={{ height: '4px' }}>
																			<div
																				className={`progress-bar ${isUrgent ? 'bg-danger' : 'bg-warning'}`}
																				role="progressbar"
																				style={{ width: `${progressPercent}%`, transition: 'width 1s linear' }}
																			></div>
																		</div>
																	</>
																) : (
																	<span className="text-muted">{t('validateRewards.Notimestamp')}</span>
																)}
															</div>
														) : (
															<span className="text-muted">-</span>
														)}
													</td>

													<td style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
														{isPending ? (
															<div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
																<button
																	className="btn btn-success btn-sm"
																	onClick={() => handleValidate(reward.reward_id)}
																	style={{ padding: '8px 16px', fontWeight: 500 }}
																>
																	<i className="mdi mdi-check-circle me-1"></i>
																	{t('validateRewards.Validate')}
																</button>
																<button
																	className="btn btn-outline-danger btn-sm"
																	onClick={() => handleExpire(reward.reward_id)}
																	style={{ padding: '8px 16px', fontWeight: 500 }}
																>
																	<i className="mdi mdi-close-circle me-1"></i>
																	{t('validateRewards.Expire')}
																</button>
															</div>
														) : isValidated ? (
															<div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
																<button
																	className="btn btn-primary btn-sm"
																	onClick={() => handleRedeem(reward.reward_id)}
																	disabled={redeemingRewardId === reward.reward_id}
																	style={{
																		padding: '8px 20px',
																		fontWeight: 500,
																		opacity: redeemingRewardId === reward.reward_id ? 0.6 : 1,
																		cursor: redeemingRewardId === reward.reward_id ? 'not-allowed' : 'pointer',
																	}}
																>
																	{redeemingRewardId === reward.reward_id ? (
																		<>
																			<span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
																			{t('validateRewards.Redeeming')}
																		</>
																	) : (
																		<>
																			<i className="mdi mdi-check me-1"></i>
																			{t('validateRewards.Redeem')}
																		</>
																	)}
																</button>
																<button
																	className="btn btn-outline-danger btn-sm"
																	onClick={() => handleExpire(reward.reward_id)}
																	style={{ padding: '8px 16px', fontWeight: 500 }}
																>
																	<i className="mdi mdi-close-circle me-1"></i>
																	{t('validateRewards.Expire')}
																</button>
															</div>
														) : isRedeemed ? (
															<span className="badge bg-soft-info text-info">
																	<i className="mdi mdi-check-all me-1"></i>
																{t('validateRewards.Redeemed')}
																</span>
														) : isExpired ? (
															<span className="badge bg-soft-danger text-danger">
																	<i className="mdi mdi-close-circle me-1"></i>
																{t('validateRewards.Expired')}
																</span>
														) : (
															<span className="text-muted">-</span>
														)}
													</td>
												</tr>
											)
										})}
										</tbody>
									</table>
								</div>
							</CardBody>
						</Card>
					)}

					{/* Status summary cards */}
					<div className="row mt-4">
						<div className="col-md-3">
							<Card className="widget-flat">
								<CardBody>
									<div className="float-end">
										<i className="mdi mdi-clock-outline widget-icon bg-warning-lighten text-warning"></i>
									</div>
									<h5 className="text-muted fw-normal mt-0">{t('validateRewards.Pending')}</h5>
									<h3 className="mt-3 mb-3">{statusCounts.PENDING}</h3>
									<p className="mb-0 text-muted"><span className="text-nowrap">{t('validateRewards.Awaitingvalidation')}</span></p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="widget-flat">
								<CardBody>
									<div className="float-end">
										<i className="mdi mdi-check-circle widget-icon bg-success-lighten text-success"></i>
									</div>
									<h5 className="text-muted fw-normal mt-0">{t('validateRewards.Validated')}</h5>
									<h3 className="mt-3 mb-3">{statusCounts.VALIDATED}</h3>
									<p className="mb-0 text-muted"><span className="text-nowrap">{t('validateRewards.Awaitingredemption')}</span></p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="widget-flat">
								<CardBody>
									<div className="float-end">
										<i className="mdi mdi-check-all widget-icon bg-info-lighten text-info"></i>
									</div>
									<h5 className="text-muted fw-normal mt-0">{t('validateRewards.Redeemed')}</h5>
									<h3 className="mt-3 mb-3">{statusCounts.REDEEMED}</h3>
									<p className="mb-0 text-muted"><span className="text-nowrap">{t('validateRewards.Completed')}</span></p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="widget-flat">
								<CardBody>
									<div className="float-end">
										<i className="mdi mdi-close-circle widget-icon bg-danger-lighten text-danger"></i>
									</div>
									<h5 className="text-muted fw-normal mt-0">{t('validateRewards.Expired')}</h5>
									<h3 className="mt-3 mb-3">{statusCounts.EXPIRED}</h3>
									<p className="mb-0 text-muted"><span className="text-nowrap">{t('validateRewards.Nolongervalid')}</span></p>
								</CardBody>
							</Card>
						</div>
					</div>

					{/* Create reward modal */}
					{showCreateModal && (
						<div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={closeCreateModal}>
							<div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
								<div className="modal-content">
									<div className="modal-header">
										<h5 className="modal-title">
											<i className="mdi mdi-gift me-2"></i>
											{t('validateRewards.CreateNewReward')}
										</h5>
										<button type="button" className="btn-close" onClick={closeCreateModal} disabled={creatingReward}></button>
									</div>
									<form onSubmit={handleCreateReward}>
										<div className="modal-body">
											<div className="mb-3">
												<label className="form-label">
													{t('validateRewards.Customer')}{' '}
													<span className="text-muted">({t('validateRewards.Optional')})</span>
												</label>
												{loadingCustomers ? (
													<div className="d-flex align-items-center">
														<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
														<span className="text-muted">{t('validateRewards.Loadingcustomers')}</span>
													</div>
												) : (
													<select
														className="form-select"
														value={createRewardForm.user_id}
														onChange={(e) => setCreateRewardForm((prev) => ({ ...prev, user_id: e.target.value }))}
														disabled={creatingReward}
													>
														<option value="">{t('validateRewards.Selectcustomer')}</option>
														{customers.map((customer) => (
															<option key={customer.id} value={customer.id}>
																{customer.username}
																{customer.first_name || customer.last_name
																	? ` (${[customer.first_name, customer.last_name].filter(Boolean).join(' ')})`
																	: ''}
																{customer.email ? ` - ${customer.email}` : ''}
															</option>
														))}
													</select>
												)}
												<small className="form-text text-muted">{t('validateRewards.selectcustomers')}</small>
											</div>

											<div className="mb-3">
												<label className="form-label">
													{t('validateRewards.RewardType')} <span className="text-danger">*</span>
												</label>
												<select
													className="form-select"
													value={createRewardForm.reward_type}
													onChange={(e) => setCreateRewardForm((prev) => ({ ...prev, reward_type: e.target.value }))}
													required
													disabled={creatingReward}
												>
													<option value="">{t('validateRewards.Selectrewardtype')}</option>
													<option value="free_coffee">{t('validateRewards.FreeCoffee')}</option>
													<option value="5_off">$5 {t('validateRewards.Off')}</option>
													<option value="10_off">$10 {t('validateRewards.Off')}</option>
													<option value="20_off">$20 {t('validateRewards.Off')}</option>
													<option value="free_item">{t('validateRewards.FreeItem')}</option>
													<option value="percentage_off">{t('validateRewards.PercentageOff')}</option>
												</select>
											</div>

											<div className="mb-3">
												<label className="form-label">{t('validateRewards.PointsUsed')}</label>
												<input
													type="number"
													className="form-control"
													value={createRewardForm.cost_points}
													onChange={(e) => setCreateRewardForm((prev) => ({ ...prev, cost_points: e.target.value }))}
													min="0"
													disabled={creatingReward}
												/>
												<small className="form-text text-muted">{t('validateRewards.pointsused')}</small>
											</div>
										</div>

										<div className="modal-footer">
											<button type="button" className="btn btn-secondary" onClick={closeCreateModal} disabled={creatingReward}>
												{t('validateRewards.Cancel')}
											</button>
											<button type="submit" className="btn btn-primary" disabled={creatingReward || !createRewardForm.reward_type}>
												{creatingReward ? (
													<>
														<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
														{t('validateRewards.Creating')}
													</>
												) : (
													<>
														<i className="mdi mdi-check me-1"></i>
														{t('validateRewards.createReward')}
													</>
												)}
											</button>
										</div>
									</form>
								</div>
							</div>
						</div>
					)}

					{/* Inline expire confirm modal */}
					{expiringRewardId && (
						<div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
							<div className="modal-dialog modal-dialog-centered modal-sm">
								<div className="modal-content">
									<div className="modal-header border-0 pb-0">
										<h5 className="modal-title">
											<i className="mdi mdi-alert-outline text-danger me-2"></i>
											{t('validateRewards.Expire')}
										</h5>
									</div>
									<div className="modal-body pt-2">
										<p className="mb-0 text-muted">
											{t('validateRewards.confirmExpire', 'Are you sure you want to expire this reward?')}
										</p>
									</div>
									<div className="modal-footer border-0 pt-0">
										<button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpiringRewardId(null)}>
											{t('validateRewards.Cancel')}
										</button>
										<button type="button" className="btn btn-danger btn-sm" onClick={confirmExpire}>
											{t('validateRewards.Expire')}
										</button>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default ValidatedRewards